<template>
    <div class="book-card">
        <a
            :href="'/editor/' + book.id"
            class="book-card__cover book-cover"
            @click.prevent="goToEditor(book.id)"
        >
            <div class="book-cover__image">
                <img class="book-cover__img" :src="book.preview_img_site" :alt="book.name" />
            </div>
            <div class="book-cover__badge" :class="bookStatusClass">
                {{ book.current_status.label }}
            </div>
            <div
                v-if="book.parent"
                class="book-cover__badge book-cover__badge--right book-cover__badge--copy"
                style="right: 0; left: auto"
            >
                Копия
            </div>
        </a>
        <div class="book-card__card">
            <div class="book-card__row">
                <div>
                    <div class="book-card__title-wrap">
                        <a
                            :href="'/editor/' + book.id"
                            class="book-card__title"
                            @click.prevent="goToEditor(book.id)"
                        >
                            {{ bookTitle }}
                        </a>
                        <button
                            v-tooltip="$t('booksList.editTooltip')"
                            type="button"
                            class="book-card__settings"
                            @click="goToSettings(book.id)"
                        >
                            {{ $t('misc.settings') }}
                        </button>
                    </div>
                    <div class="book-card__author">
                        {{ authorName }}
                    </div>
                </div>
                <div class="book-card__links">
                    <BooksBuilds :book-id="book.id" checking-parts spread-book-builds>
                        <template #trigger="{ start }">
                            <button
                                v-t="'misc.download'"
                                type="button"
                                class="book-card__link"
                                @click.prevent="start"
                            />
                        </template>
                    </BooksBuilds>
                    <button
                        v-t="'misc.editor'"
                        type="button"
                        class="book-card__link"
                        @click="goToEditor(book.id)"
                    />
                    <ActionModal
                        :method="deleteBook"
                        action-data-field="id"
                        success-text="Book deleted"
                        :title="'Delete book'"
                        action-text="Delete"
                        :item="book"
                        @delete="$emit('deleted', book.id)"
                    >
                        <template #trigger>
                            <span class="book-card__link book-card__link--red">
                                {{ $t('misc.delete') }}
                            </span>
                        </template>
                        <template #default>
                            <div class="book-del-modal__text">
                                {{ $t('deleteBook.text', { name: book.name }) }}
                            </div>
                        </template>
                    </ActionModal>
                </div>
            </div>
            <div class="book-card__row">
                <div class="book-card__info">
                    <div class="book-card__table">
                        <div class="book-card__table-row">
                            <div class="book-card__table-col">
                                <div class="book-card__label">ISBN</div>
                            </div>
                            <div class="book-card__table-col">
                                <span class="book-card__text">
                                    {{ book.isbnx }}
                                </span>
                            </div>
                        </div>
                        <div class="book-card__table-row">
                            <div class="book-card__table-col">
                                <div class="book-card__label">{{ $t('misc.language') }}</div>
                            </div>
                            <div class="book-card__table-col">
                                <span class="book-card__text">
                                    {{ Object.values(book.book_language)[0] }}
                                </span>
                            </div>
                        </div>
                        <div class="book-card__table-row">
                            <div class="book-card__table-col">
                                <div class="book-card__label">{{ $t('booksList.narrator') }}</div>
                            </div>
                            <div class="book-card__table-col">
                                <span class="book-card__text">
                                    {{ book.speaker ? book.speaker.name[$i18n.locale] : 'None' }}
                                </span>
                            </div>
                        </div>
                        <div class="book-card__table-row">
                            <div class="book-card__table-col">
                                <div class="book-card__label">{{ $t('misc.category') }}</div>
                            </div>
                            <div class="book-card__table-col">
                                <span class="book-card__text">
                                    {{ bookCategory }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="book-card__table">
                        <div class="book-card__table-row">
                            <div class="book-card__table-col">
                                <div class="book-card__label">{{ $t('booksList.size') }}</div>
                            </div>
                            <div class="book-card__table-col">
                                <span class="book-card__text">
                                    {{ bookSize(book) }}
                                </span>
                            </div>
                        </div>
                        <div class="book-card__table-row">
                            <div class="book-card__table-col">
                                <div class="book-card__label">
                                    {{ $t('misc.lastChange') }}
                                </div>
                            </div>
                            <div class="book-card__table-col">
                                <span class="book-card__text">
                                    {{ formatDdmmyyyy(book.modified) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <AdminBookControls
            v-if="showAdminControls"
            :book-id="book.id"
            @closed="showAdminControls = false"
        />
    </div>
</template>

<script>
import { formatDate } from '@speechki-frontend/shared/helpers/functions';
import { ActionModal } from '@speechki-frontend/shared/ui/modals';

import { AdminBookControls } from '../';
import BooksBuilds from '@speechki-frontend/core/editor/components/BooksBuilds/BooksBuilds';

import { deleteBook } from '../../api/books';

export default {
    name: 'BookCard',
    components: {
        AdminBookControls,
        BooksBuilds,
        ActionModal,
    },
    props: {
        book: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            showExportModal: false,
            showAdminControls: false,
        };
    },
    computed: {
        bookTitle() {
            return this.book.name.length > 60
                ? `${this.book.name.slice(0, 60)}...`
                : this.book.name;
        },
        authorName() {
            if (!this.book.authors || !this.book.authors.length)
                return this.$t('booksList.authorUnknown');
            return this.book.authors.reduce((prev, acc, index) => {
                return (
                    prev +
                    `${acc.first_name} ${acc.last_name}${
                        index < this.book.authors.length - 1 ? ', ' : ''
                    } `
                );
            }, '');
        },
        bookCategory() {
            if (!this.book.categories || !this.book.categories.length)
                return this.$t('booksList.noCategory');
            let str = this.book.categories[0].label[this.$i18n.locale];
            if (this.book.categories.length > 1) {
                str += ', ...';
            }
            return str;
        },
        bigTitle() {
            return this.book.name.length > 45;
        },
        bookStatusClass() {
            if (this.book.current_status.name === 'trial') {
                return 'book-cover__badge--red';
            }
            if (this.book.current_status.name === 'draft') {
                return 'book-cover__badge--yellow';
            }
            if (this.book.current_status.name === 'announcement') {
                return 'book-cover__badge--pink';
            }
            if (this.book.current_status.name === 'published') {
                return 'book-cover__badge--green';
            }
            return '';
        },
    },
    methods: {
        deleteBook,
        formatDdmmyyyy(value) {
            return formatDate(value, { pattern: 'dd.MM.yyyy' });
        },
        goToEditor(id) {
            this.$router.push({ name: 'editor', params: { id } });
        },
        goToSettings(id) {
            this.$router.push({ name: 'book-settings', params: { id } });
        },
        bookSize(book) {
            const k = book.symbols / 1000;
            if (k < 1) {
                return k.toFixed(1) + this.$t('bookEdit.size');
            }
            return Math.round(book.symbols / 1000) + this.$t('bookEdit.size');
        },
    },
};
</script>

<style lang="scss" src="./BookCard.scss"></style>
